import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Separator } from '@ordo/ui/separator'
import { Skeleton } from '@ordo/ui/skeleton'
import { BackLink } from 'compositions/BackLink'
import { ConfirmDialog } from 'compositions/ConfirmDialog'
import { LoadingWrapper } from 'compositions/LoadingWrapper'
import { Page } from 'compositions/Page'
import { useDeleteSender, useResendSenderVerification, useSender, useUpdateSender } from 'hooks/api'
import { Save, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { isSmsSender, type Sender } from 'types/senders'

import {
  diffSenderPayload,
  EMPTY_SENDER_FORM,
  isSenderFormValid,
  type SenderFormData,
  senderToFormData,
} from './sender-form'
import { SenderFormFields } from './sender-form-fields'
import { VerificationBanner } from './sender-verification'

function SenderDetailContent({ sender, refetch }: { sender: Sender; refetch: () => void }) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { perform: updateSender, loading: saving } = useUpdateSender()
  const { perform: deleteSender } = useDeleteSender()
  const { perform: resendVerification, loading: resending } = useResendSenderVerification()

  const [formData, setFormData] = useState<SenderFormData>(EMPTY_SENDER_FORM)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setFormData(senderToFormData(sender))
  }, [sender])

  const handleFieldChange = useCallback((field: keyof SenderFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleSave = useCallback(async () => {
    if (!id) {
      return
    }
    setError(null)
    const payload = diffSenderPayload(formData, sender)
    if (Object.keys(payload).length === 0) {
      return
    }
    try {
      await updateSender(id, payload)
      refetch()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update sender')
    }
  }, [id, formData, sender, updateSender, refetch])

  const handleDelete = useCallback(async () => {
    if (!id) {
      return
    }
    await deleteSender(id)
    navigate('/admin/senders')
  }, [id, deleteSender, navigate])

  const handleResend = useCallback(async () => {
    if (!id) {
      return
    }
    setError(null)
    try {
      await resendVerification(id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend verification email')
    }
  }, [id, resendVerification])

  const dirty = Object.keys(diffSenderPayload(formData, sender)).length > 0
  const valid = isSenderFormValid(formData)
  const canSave = dirty && valid && !saving && !sender.locked

  return (
    <div className="space-y-4">
      <VerificationBanner sender={sender} onResend={handleResend} resending={resending} />

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <CardTitle>{sender.nickname}</CardTitle>
            {sender.locked ? (
              <Badge variant="secondary">{isSmsSender(sender) && sender.isDefault ? 'Default' : 'Locked'}</Badge>
            ) : null}
          </div>
          <ConfirmDialog
            title={isSmsSender(sender) ? 'Deactivate SMS sender' : 'Delete sender'}
            description={
              isSmsSender(sender)
                ? `Deactivate "${sender.nickname}"? Sends selecting this number will stop until the sender is active again.`
                : `Delete "${sender.nickname}"? Any campaigns or transactional sends still pointing at this sender will fail.`
            }
            onConfirm={handleDelete}
            trigger={
              <Button variant="destructive" size="sm" disabled={isSmsSender(sender) && sender.locked}>
                <Trash2 className="mr-1 h-4 w-4" />
                {isSmsSender(sender) ? 'Deactivate' : 'Delete'}
              </Button>
            }
          />
        </CardHeader>
        <Separator />
        <CardContent className="pt-6">
          <SenderFormFields
            data={formData}
            existing
            onChange={handleFieldChange}
            disabled={saving || sender.locked}
            fromEmailHelper="Changing this triggers a new SendGrid verification email and resets the verified state."
          />

          {error ? (
            <p className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </p>
          ) : null}

          <div className="mt-6 flex justify-end">
            <Button onClick={handleSave} disabled={!canSave}>
              <Save className="mr-1 h-4 w-4" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export function AdminSenderDetail() {
  const { id } = useParams<{ id: string }>()
  const { data: sender, loading, refetch } = useSender(id)

  if (!loading && !sender) {
    return (
      <Page>
        <div className="mb-4">
          <BackLink to="/admin/senders">Back to Senders</BackLink>
        </div>
        <div className="py-12 text-center text-muted-foreground">Sender not found</div>
      </Page>
    )
  }

  return (
    <Page>
      <div className="mb-4">
        <BackLink to="/admin/senders">Back to Senders</BackLink>
      </div>
      <LoadingWrapper
        loading={loading}
        loadingElement={
          <div className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
              </CardHeader>
              <Separator />
              <CardContent className="space-y-4 pt-6">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          </div>
        }>
        {sender ? <SenderDetailContent sender={sender} refetch={refetch} /> : null}
      </LoadingWrapper>
    </Page>
  )
}
